import { Button, Space, Table, Typography, Flex } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import { StyledButton } from "../../Common/Components";
import ConfirmationModal from "../../Common/Components/ConfirmationModal";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import { useDeleteLeadersQualification, useUpdateLeadersQualification } from "../../entities/api";
import { useFetchLeadersQualificationsPagination } from "../../entities/api/useLeadersQualificationAPI";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "../../hooks/useHandlePagination";

const PermissionName = PermissionNames.LEADER_QUALIFIER;

const columnsone = ({
  customerId,
  sport,
  organizationId,
  formatMessage,
  handleOpenConfirmation,
  handleOpenEditModal,
}) => {
  return [
    {
      title: formatMessage({
        id: "name",
        defaultMessage: "Name",
      }),
      dataIndex: "name",
      width: "70px",
    },
    {
      render: (_, record) => (
        <div style={{ textAlign: "right" }}>
          <PermissionCheck permissionKeys={[PermissionNames.LEADER_CRITERIA_SET]}>
            <Link
              to={`/${customerId}/admin/${sport}/${organizationId}/leaders-qualification/${record.leaderCriteriaId}/qualifiers`}
            >
              <StyledButton type={"text"}>
                {formatMessage({
                  id: "rules",
                  defaultMessage: "Rules",
                })}
              </StyledButton>
            </Link>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenEditModal(record);
              }}
            >
              {formatMessage({
                id: "edit",
                defaultMessage: "edit",
              })}
            </StyledButton>
          </PermissionCheck>
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
            <StyledButton
              type={"text"}
              onClick={() => {
                handleOpenConfirmation(record);
              }}
            >
              <i data-testid="delete-icon" className={`fas fa-trash-can`} />
            </StyledButton>
          </PermissionCheck>
          .
        </div>
      ),
      width: 1,
    },
  ];
};

const EditModal = ({ isOpen, setIsOpen, handleClose, record, handleOk }) => {
  const [value, setValue] = useState(record.name);

  const handleChange = useCallback((event) => {
    setValue(event.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    handleOk({ name: value });
  }, [value, handleOk]);

  return (
    <ConfirmationModal
      title="Edit"
      setIsModalOpen={setIsOpen}
      isModalOpen={isOpen}
      handleOk={handleSubmit}
      handleCancel={handleClose}
      okText="Ok"
    >
      <StyledInput type="text" value={value} onChange={handleChange}></StyledInput>
    </ConfirmationModal>
  );
};

const LeadersQualification = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { sport, organizationId, customerId } = useParams();
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination();

  const [selected, setSelected] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const { mutate: deleteFn, isPending } = useDeleteLeadersQualification();
  const { mutate: updateFn } = useUpdateLeadersQualification();
  const { data: leadersQualificationData, isLoading } = useFetchLeadersQualificationsPagination({
    limit: pageSize,
    offset: offset,
    nameContains: searchText,
  });

  const data = useMemo(() => leadersQualificationData?.data || [], [leadersQualificationData?.data]);
  const nextLink = useMemo(() => leadersQualificationData?.links?.next, [leadersQualificationData?.links?.next]);

  useEffect(() => {
    setFilteredData(data);
  }, [data, setFilteredData, searchText]);

  const handleOpenConfirmation = useCallback(
    (record) => {
      setSelected(record);
      setShowConfirmation(true);
    },
    [setSelected, setShowConfirmation],
  );

  const handleOpenEditModal = useCallback(
    (record) => {
      setSelected(record);
      setIsEditModalOpen(true);
    },
    [setIsEditModalOpen, setSelected],
  );

  const handleEdit = useCallback(
    (data) => {
      updateFn({ data, leaderCriteriaId: selected?.leaderCriteriaId });
    },
    [updateFn, selected?.leaderCriteriaId],
  );

  const handleDelete = useCallback(() => {
    deleteFn(selected?.leaderCriteriaId);
  }, [deleteFn, selected?.leaderCriteriaId]);

  return (
    <Flex vertical gap={8}>
      <Flex align="center" justify="space-between">
        {isEditModalOpen && (
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
            <EditModal
              setIsOpen={setIsEditModalOpen}
              handleOk={handleEdit}
              record={selected}
              isOpen={isEditModalOpen}
              handleClose={() => {
                setIsEditModalOpen(false);
              }}
            />
          </PermissionCheck>
        )}
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.DELETE]}>
          <ConfirmationModal
            title="Delete Leaders Qualification"
            setIsModalOpen={setShowConfirmation}
            isModalOpen={showConfirmation}
            handleOk={handleDelete}
            handleCancel={() => {
              setSelected({});
            }}
            okText="Delete"
          >
            <div>{`You are about to delete ${selected.name}. Is this OK?`}</div>
          </ConfirmationModal>
        </PermissionCheck>
        <Typography.Title level={4}>
          {formatMessage({
            id: "leaders.qualification",
            defaultMessage: "Leaders Qualification",
          })}
        </Typography.Title>
        <Space>
          <SearchFilter searchValue={searchText} doSearch={setSearchText} />
          <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.CREATE]}>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/leaders-qualification/manage/`}>
              <Button type="primary" icon={<i className={"fas fa-plus"} style={{ marginRight: "5px" }} />}>
                Leaders Qualification
              </Button>
            </Link>
          </PermissionCheck>
        </Space>
      </Flex>
      <Table
        loading={isPending || isLoading}
        size={"small"}
        columns={columnsone({
          customerId,
          sport,
          organizationId,
          formatMessage,
          handleOpenConfirmation,
          handleOpenEditModal,
        })}
        rowKey={"leaderCriteriaId"}
        dataSource={filteredData}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions,
          showQuickJumper: false,
          total: nextLink ? offset + data.length + 1 : data.length + offset,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
          ),
        }}
      />
    </Flex>
  );
};

const StyledInput = styled.input`
  display: block;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

export default LeadersQualification;
